import { TemplateResult, html, nothing, PropertyValueMap, css } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { RapidElement } from '../RapidElement';
import { CustomEventType } from '../interfaces';
import { TicketEvent } from '../events';
import { DEFAULT_AVATAR } from '../webchat/assets';

const BATCH_TIME_WINDOW = 60 * 60 * 1000;
const SCROLL_FETCH_BUFFER = 200; // pixels from top
const MIN_FETCH_TIME = 250;

const getUnsendableReasonMessage = (reason: string): string => {
  switch (reason) {
    case 'no_route':
      return 'No channel available to send message';
    case 'contact_blocked':
      return 'Contact has been blocked';
    case 'contact_stopped':
      return 'Contact has been stopped';
    case 'contact_archived':
      return 'Contact is archived';
    case 'org_suspended':
      return 'Workspace is suspended';
    case 'looping':
      return 'Message loop detected';
    default:
      return 'Unable to send message';
  }
};

const getStatusReasonMessage = (reason: string): string => {
  switch (reason) {
    case 'error_limit':
      return 'Error limit reached';
    case 'too_old':
      return 'Message is too old to send';
    case 'channel_removed':
      return 'Channel was removed';
    default:
      return 'Message failed to send';
  }
};

export enum MessageType {
  Inline = 'inline',
  Error = 'error',
  Collapse = 'collapse',
  Note = 'note'
}

export type GroupReason =
  | 'time_elapsed'
  | 'new_author'
  | 'new_type'
  | 'initial';

export interface MessageGroup {
  messages: string[];
  reason: GroupReason;
}

export interface ObjectReference {
  uuid: string;
  name: string;
}

interface User extends ObjectReference {
  avatar?: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface Msg {
  text: string;
  channel: ObjectReference;
  quick_replies: string[];
  urn: string;
  direction: string;
  type: string;
  attachments: string[];
  unsendable_reason?:
    | 'no_route'
    | 'contact_blocked'
    | 'contact_stopped'
    | 'contact_archived'
    | 'org_suspended'
    | 'looping';
}

export interface ContactEvent {
  uuid?: string;
  type: string;
  created_on: Date;
  _user?: User;
  _rendered?: { html: TemplateResult; type: MessageType };
}

export interface MsgEvent extends ContactEvent {
  msg: Msg;
  optin?: ObjectReference;
  _status?: {
    created_on: string;
    status: 'wired' | 'sent' | 'delivered' | 'read' | 'errored' | 'failed';
    reason: 'error_limit' | 'too_old' | 'channel_removed';
  };
  _deleted?: {
    created_on: string;
    by_contact: boolean;
    user?: { name: string; uuid: string };
  };
  _logs_url?: string;
}

const TIME_FORMAT = { hour: 'numeric', minute: '2-digit' } as any;
const VERBOSE_FORMAT = {
  weekday: undefined,
  year: undefined,
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
} as any;
const VERBOSE_FORMAT_WITH_YEAR = {
  weekday: undefined,
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
} as any;

export class Chat extends RapidElement {
  static get styles() {
    return css`
      :host {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        position: relative;
        z-index: 1;
      }

      slot[name='header'] {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        display: block;
      }

      slot[name='footer'] {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        display: block;
        /* The slot overlays the bottom of the chat history, so clicks
           on the chat scrollbar or messages behind it must pass
           through. Slotted footer content can opt back in with
           pointer-events: auto on its interactive bits. */
        pointer-events: none;
      }

      .block {
        margin-bottom: 1em;
        display: flex;
        flex-direction: row;
      }

      .block.outgoing {
        flex-direction: row-reverse;
      }

      .block.collapse {
        margin: 0;
        align-items: center;
        display: flex;
        flex-direction: column;
        margin-bottom: 0.5em;
      }

      .block.collapse .messsage {
        transform: scaleY(0);
        margin: 0;
        padding: 0;
        line-height: 0;
      }

      .time {
        text-align: center;
        font-size: 0.8em;
        color: #999;
        margin-bottom: 2em;
        margin-top: 1em;
        border-top: 1px solid #e9e9e9;
        padding: 1em;
        margin-left: 10%;
        margin-right: 10%;
      }

      .time.first {
        border-top: none;
        margin-top: 0;
        border-bottom: 1px solid #e9e9e9;
        margin-bottom: 2em;
      }

      .first .time {
        margin-top: 0;
        border-top: none;
        padding-top: 0;
      }

      .group-reason {
        text-align: center;
        font-size: 0.75em;
        color: #999;
        margin-bottom: 1em;
        margin-top: 0.5em;
        padding: 0.5em 1em;
        font-style: italic;
      }

      .row {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        margin-bottom: 8px;
      }

      .row.is-event {
        margin-bottom: 2px;
        margin-left: 0px !important;
        margin-right: 4px !important;
      }

      .input-panel {
        padding: 1em;
        background: #fff;
      }

      temba-user {
        margin-right: 0.6em;
        margin-left: 0.6em;
        width: 2em;
        align-self: flex-end;
      }

      .toggle {
        flex-shrink: 0;
        width: 4em;
        height: 4em;
        overflow: hidden;
        border-radius: 100%;
        box-shadow:
          rgba(0, 0, 0, 0.1) 0px 0px 1em 0.7em,
          rgba(0, 0, 0, 0.2) 0px 1px 2px 0px,
          inset 0 0 0 0.25em rgba(0, 0, 0, 0.1);
        cursor: pointer;
        transition: box-shadow var(--toggle-speed, 200ms) ease-out;
        position: absolute;
        bottom: 1em;
        right: 1em;
      }

      .toggle:hover {
        box-shadow:
          rgba(0, 0, 0, 0.1) 0px 0px 1em 0.7em,
          rgba(0, 0, 0, 0.4) 0px 1px 2px 0px,
          inset 0 0 0 0.25em rgba(0, 0, 0, 0.2);
      }

      .incoming .row {
        flex-direction: row-reverse;
        margin-left: 1em;
      }

      .bubble {
        padding: 10px 14px;
        background: var(--color-chat-in, #e5e5ea);
        border-radius: 18px;
        border: var(--chat-border-in, none);
      }

      .bubble .name {
        font-size: 0.95em;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.4);
        margin-bottom: 0.25em;
      }

      .outgoing .latest .bubble {
        border-bottom-left-radius: 4px;
      }

      .incoming .bubble-wrap {
        align-items: flex-end;
      }

      .incoming .bubble {
        background: var(--color-chat-out, #007aff);
        border: var(--chat-border-out, none);
        color: white;
      }

      .incoming .latest .bubble {
        border-bottom-right-radius: 4px;
      }

      .incoming .bubble .name {
        color: rgba(255, 255, 255, 0.7);
      }

      .note .bubble {
        background: #fffac3;
        border: 1px solid #e8d169;
        color: #5d4e1e;
      }

      .note .bubble .name {
        color: rgba(93, 78, 30, 0.65);
      }

      .note-time {
        margin-top: 0.35em;
        font-size: 0.9em;
        color: rgba(93, 78, 30, 0.65);
      }

      .warning .bubble {
        background: #fef3c7;
        color: rgba(125, 87, 18, 0.8);
        border: none;
      }

      .warning .bubble .name {
        color: rgba(125, 87, 18, 0.6);
      }

      .failed .bubble,
      .error .bubble {
        border: none;
        background: #fee3e3;
        color: #c01829;
      }

      .error .bubble .name,
      .failed .bubble .name {
        color: rgba(192, 24, 41, 0.6);
      }

      .deleted .bubble {
        background: #fff;
        color: #999;
        border: 1px solid #e0e0e0;
      }

      .deleted .bubble .name {
        color: #aaa;
      }

      .message-text {
        white-space: pre-wrap;
        margin-bottom: 0;
        line-height: 1.2;
        word-break: break-word;
        font-size: 13px;
      }

      .message-deleted {
        font-style: italic;
        margin-bottom: 0;
        line-height: 1.2;
        font-size: 13px;
      }

      .quick-replies {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 6px;
        padding: 8px 0;
        margin: 0 auto;
        max-width: 90%;
      }

      .quick-reply-btn {
        padding: 4px 8px;
        border-radius: 18px;
        border: 1px solid var(--color-chat-out, #007aff);
        background: white;
        color: var(--color-chat-out, #007aff);
        font-size: 11px;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .quick-reply-btn:hover:not(:disabled) {
        background: var(--color-chat-out, #007aff);
        color: white;
      }

      .quick-reply-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .chat {
        width: 28rem;
        border-radius: var(--curvature);
        overflow: hidden;
        box-shadow:
          rgba(0, 0, 0, 0.1) 0px 3px 7px 0px,
          rgba(0, 0, 0, 0.2) 0px 1px 2px 0px,
          rgba(0, 0, 0, 0.1) 5em 5em 5em 5em;
        position: absolute;
        bottom: 3em;
        right: 1em;
        transition: all var(--toggle-speed, 200ms) ease-out;
        transform: scale(0.9);
        pointer-events: none;
        opacity: 0;
      }

      .chat.open {
        bottom: 6em;
        opacity: 1;
        transform: scale(1);
        pointer-events: initial;
      }

      .messages {
        position: relative;
        flex-grow: 1;
        overflow: hidden;
      }

      .scroll {
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        overflow-scrolling: touch;
        padding: var(--chat-top-padding, 1em) 1em
          var(--chat-bottom-padding, 2.5em) 1em;
        display: flex;
        flex-direction: column-reverse;
      }

      :host(.phone-screen) .scroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      :host(.phone-screen) .scroll::-webkit-scrollbar {
        display: none;
      }

      /* Top/bottom scroll-shadow indicators. Decorative only — they
         must not intercept clicks (would otherwise block the chat
         scrollbar and the bottom edge of the messages area). */
      .messages:before {
        content: '';
        background: radial-gradient(
            farthest-side at 50% 0,
            rgba(0, 0, 0, 0.2),
            rgba(0, 0, 0, 0)
          )
          center top;
        height: 10px;
        display: block;
        position: absolute;
        width: 100%;
        transition: opacity var(--toggle-speed, 200ms) ease-out;
        z-index: 1;
        pointer-events: none;
      }

      .messages:after {
        content: '';
        background: radial-gradient(
            farthest-side at 50% 100%,
            rgba(0, 0, 0, 0.2),
            rgba(0, 0, 0, 0)
          )
          center bottom;
        height: 10px;
        display: block;
        position: absolute;
        bottom: 0;
        margin-top: -10px;
        width: 100%;
        margin-right: 5em;
        transition: opacity var(--toggle-speed, 200ms) ease-out;
        z-index: 1;
        pointer-events: none;
      }

      .bubble-wrap {
        position: relative;
        max-width: 70%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .scroll-at-top.messages:before {
        opacity: 0;
      }

      .scroll-at-bottom.messages:after {
        opacity: 0;
      }

      .input {
        border: none;
        flex-grow: 1;
        color: #333;
        font-size: 1em;
      }

      .input:focus {
        outline: none;
      }

      input::placeholder {
        opacity: 0.3;
      }

      .input.inactive {
        // pointer-events: none;
        // opacity: 0.3;
      }

      .active {
      }

      .send-icon {
        color: #eee;
        pointer-events: none;
        transform: rotate(-45deg);
        transition: transform 0.2s ease-out;
      }

      .pending .send-icon {
        color: var(--color-primary-dark);
        pointer-events: initial;
        transform: rotate(0deg);
      }

      .notice {
        padding: 1em;
        background: #f8f8f8;
        color: #666;
        text-align: center;
        cursor: pointer;
      }

      .connecting .notice {
        display: flex;
        justify-content: center;
      }

      .connecting .notice temba-icon {
        margin-left: 0.5em;
      }

      .reconnect {
        color: var(--color-primary-dark);
        text-decoration: underline;
        font-size: 0.9em;
      }

      .input:disabled {
        background: transparent !important;
      }

      temba-loading {
        justify-content: center;
        margin: 0.5em auto;
        margin-bottom: 2em;
      }

      temba-loading.hidden {
        display: none;
      }

      .inline {
      }

      .event {
        flex-grow: 1;
        align-self: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        font-size: 11px;
        color: #8e8e93;
        max-width: 100%;
        overflow: hidden;
      }

      .event .webhook-event {
        display: inline-flex;
        align-items: flex-start;
        gap: 6px;
        max-width: 100%;
        min-width: 0;
      }

      .event .webhook-event-text {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        word-break: break-all;
        min-width: 0;
        padding: 4px 0;
      }

      .event .webhook-event-url {
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
      }

      .event .webhook-event-url:hover {
        text-decoration: none;
      }

      .event p {
        margin: 0;
        padding: 0;
      }

      .event strong {
        font-weight: 500;
      }

      .collapse {
      }

      a {
        color: var(--color-primary-dark);
      }

      .attachments {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        align-self: flex-start;
      }

      .incoming .attachments {
        align-self: flex-end;
      }

      temba-thumbnail {
        margin: 0.4em;
        border-radius: var(--curvature);
      }

      .failed temba-thumbnail,
      .error temba-thumbnail {
        --thumb-background: #ffe6e6;
        --thumb-border: var(--color-error);
        border: 1px solid var(--color-error);
        color: #ad4747a8;
      }

      .outgoing .popup {
        justify-content: left;
      }

      .incoming .popup {
        justify-content: right;
      }

      .popup {
        display: flex;
        position: absolute;
        background: #fff;
        margin: 0;
        padding: 0.5em 1em;
        border-radius: var(--curvature);
        box-shadow:
          rgba(0, 0, 0, 0.05) 0px 3px 7px 0px,
          rgba(0, 0, 0, 0.2) 0px 1px 2px 0px;
        border: 1px solid #f3f3f3;
        opacity: 0;
        z-index: 2;
      }

      .popup .arrow {
        z-index: 1;
        text-shadow: 0px 3px 3px rgba(0, 0, 0, 0.1);
        position: absolute;
        justify-content: center;
        text-align: center;
        font-size: 1.3em;
        transform: translateY(0.7em) scale(1);
        color: #fff;
        bottom: 0;
      }

      .bubble-wrap:hover .popup {
        opacity: 1;
        transition-delay: 1s;
        top: -35px;
      }

      .new-message-notification {
        position: absolute;
        bottom: 1em;
        left: 50%;
        transform: translateX(-50%) translateY(100px);
        background: var(--color-primary-dark, #3c92dd);
        color: white;
        padding: 0.75em 1.5em;
        border-radius: var(--curvature);
        box-shadow:
          rgba(0, 0, 0, 0.2) 0px 3px 7px 0px,
          rgba(0, 0, 0, 0.3) 0px 1px 2px 0px;
        cursor: pointer;
        opacity: 0;
        transition: all 0.3s ease-out;
        z-index: 100;
        font-weight: 500;
        pointer-events: none;
      }

      .new-message-notification.visible {
        transform: translateX(-50%) translateY(0);
        opacity: 1;
        pointer-events: auto;
      }

      .new-message-notification:hover {
        background: var(--color-primary-darker, #2b7ac4);
        box-shadow:
          rgba(0, 0, 0, 0.3) 0px 4px 10px 0px,
          rgba(0, 0, 0, 0.4) 0px 2px 4px 0px;
      }

      mark {
        background: #fef08a;
        color: #1a1a1a;
        padding: 0.1em 0.3em;
        margin: 0 0.1em;
        border-radius: 3px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
      }

      .search-match .bubble {
        box-shadow: 0 0 0 2px #fef08a;
      }

      @keyframes search-pulse {
        0% {
          box-shadow: 0 0 0 2px #fef08a;
        }
        50% {
          box-shadow: 0 0 0 4px #fef08a;
        }
        100% {
          box-shadow: 0 0 0 2px #fef08a;
        }
      }

      .search-match .bubble {
        animation: search-pulse 1s ease-in-out 2;
      }
    `;
  }

  @property({ type: Array })
  messageGroups: MessageGroup[] = [];

  @property({ type: Boolean })
  fetching = false;

  @property({ type: Boolean, attribute: false })
  hideTopScroll = true;

  @property({ type: Boolean, attribute: false })
  hideBottomScroll = true;

  @property({ type: String, attribute: 'avatar' })
  defaultAvatar = DEFAULT_AVATAR;

  @property({ type: Boolean })
  agent = false;

  @property({ type: Boolean })
  avatars = false;

  // identity of the contact this chat belongs to, used to render a
  // name-based avatar for the contact's own incoming messages (which the
  // backend does not attach a `_user` to)
  @property({ type: String })
  contactName: string;

  @property({ type: String })
  contactUuid: string;

  @property({ type: Boolean, attribute: false })
  endOfHistory = false;

  @property({ type: Object, attribute: false })
  oldestEventDate: Date = null;

  @property({ type: Boolean, attribute: false })
  showNewMessageNotification = false;

  @property({ type: Object })
  showMessageLogsAfter: Date = null;

  @property({ type: Boolean })
  hasFooter = false;

  @property({ type: Boolean })
  showTimestamps = true;

  @property({ type: String, attribute: false })
  searchHighlight: string = null;

  @property({ type: String, attribute: false })
  highlightMessageUuid: string = null;

  private msgMap = new Map<string, ContactEvent>();
  private metadataCache = new Map<string, ContactEvent>();

  public firstUpdated(
    changed: PropertyValueMap<any> | Map<PropertyKey, unknown>
  ): void {
    super.firstUpdated(changed);
    const scroll = this.shadowRoot.querySelector('.scroll');
    const hasScroll = scroll.scrollHeight > scroll.clientHeight;
    this.hideBottomScroll = true;
    this.hideTopScroll = !hasScroll;
  }

  public addMessages(
    messages: ContactEvent[],
    startTime: Date = null,
    append = false,
    maintainScroll = false
  ) {
    if (!startTime) {
      startTime = new Date();
    }

    const elapsed = new Date().getTime() - startTime.getTime();
    window.setTimeout(
      () => {
        this.fetching = false;
        // first add messages to the map
        const newMessages = [];
        for (const m of messages) {
          // filter out metadata events - they aren't rendered but cached for later reference
          if (m.type === 'msg_deleted' || m.type === 'msg_status_changed') {
            const msgUuid = (m as any).msg_uuid;
            if (msgUuid) {
              this.metadataCache.set(msgUuid, m);
            }
            continue;
          }

          if (this.addMessage(m)) {
            newMessages.push(m.uuid);
          }
        }

        if (newMessages.length === 0) {
          return;
        }

        const ele = this.shadowRoot.querySelector('.scroll');
        const prevTop = ele.scrollTop;
        const prevScrollHeight = ele.scrollHeight;
        const scrollableHeight = ele.scrollHeight - ele.clientHeight;
        const isScrolledAway =
          scrollableHeight > 0 && Math.abs(ele.scrollTop) > 50;

        const grouped = this.groupMessages(newMessages);
        this.insertGroups(grouped, append);

        // show notification if new messages are appended and user is scrolled away from bottom
        // but not during search (searchHighlight is set)
        if (
          append &&
          isScrolledAway &&
          newMessages.length > 0 &&
          !this.searchHighlight
        ) {
          this.showNewMessageNotification = true;
        }

        window.setTimeout(() => {
          // when appending (new messages at bottom), adjust scroll to maintain visible content
          // with column-reverse, new content at bottom increases scrollHeight
          if (append && (isScrolledAway || maintainScroll)) {
            const heightDiff = ele.scrollHeight - prevScrollHeight;
            ele.scrollTop = prevTop - heightDiff;
          } else {
            ele.scrollTop = prevTop;
          }

          // recalculate shadow visibility after content change
          const scrollableHeight = ele.scrollHeight - ele.clientHeight;
          const absScrollTop = Math.abs(ele.scrollTop);
          this.hideTopScroll = absScrollTop >= scrollableHeight - 1;
          this.hideBottomScroll = absScrollTop <= 1;

          this.fireCustomEvent(CustomEventType.FetchComplete);
        }, 100);
      },
      // if it's the first load don't wait, otherwise wait a minimum amount of time
      this.messageGroups.length === 0
        ? 0
        : Math.max(0, MIN_FETCH_TIME - elapsed)
    );
  }

  /**
   * Synchronously loads messages into the chat without any timeouts or scroll
   * adjustments. Use this when the caller controls visibility and scroll
   * positioning (e.g. search result navigation).
   */
  public loadMessages(messages: ContactEvent[]) {
    const newMessageIds: string[] = [];
    for (const m of messages) {
      if (m.type === 'msg_deleted' || m.type === 'msg_status_changed') {
        const msgUuid = (m as any).msg_uuid;
        if (msgUuid) {
          this.metadataCache.set(msgUuid, m);
        }
        continue;
      }
      if (this.addMessage(m)) {
        newMessageIds.push(m.uuid);
      }
    }
    if (newMessageIds.length > 0) {
      const grouped = this.groupMessages(newMessageIds);
      this.insertGroups(grouped, false);
    }
  }

  private addMessage(msg: ContactEvent): boolean {
    const isNew = !this.messageExists(msg);
    this.msgMap.set(msg.uuid, msg);
    return isNew;
  }

  public messageExists(msg: ContactEvent): boolean {
    return this.msgMap.has(msg.uuid);
  }

  private isSameGroup(
    msg1: ContactEvent,
    msg2: ContactEvent,
    lastTimeElapsedDate?: Date
  ): { same: boolean; reason?: GroupReason } {
    if (!msg1 || !msg2) {
      return { same: true };
    }

    // for type equivalence, treat all non-message types as the same.
    // Notes are treated like messages so they group with other notes
    // only when type + author match (and split from non-note events).
    const isMsg1 =
      msg1.type === 'msg_created' ||
      msg1.type === 'msg_received' ||
      msg1.type === 'ivr_created' ||
      msg1.type === 'ticket_note_added';
    const isMsg2 =
      msg2.type === 'msg_created' ||
      msg2.type === 'msg_received' ||
      msg2.type === 'ivr_created' ||
      msg2.type === 'ticket_note_added';
    const typeMatch =
      isMsg1 && isMsg2 ? msg1.type === msg2.type : isMsg1 === isMsg2;

    // check time first - if BATCH_TIME_WINDOW has passed since last time_elapsed reason
    // compare the current message (msg1) against the last break point to detect
    // when messages have drifted far enough to warrant a new time group
    const timeToCheck = lastTimeElapsedDate || msg2.created_on;
    if (
      Math.abs(msg1.created_on.getTime() - timeToCheck.getTime()) >=
      BATCH_TIME_WINDOW
    ) {
      return { same: false, reason: 'time_elapsed' };
    }

    if (!typeMatch) {
      return { same: false, reason: 'new_type' };
    }

    // only check author for message types
    if (isMsg1 && isMsg2 && msg1._user?.name !== msg2._user?.name) {
      return { same: false, reason: 'new_author' };
    }

    return { same: true };
  }

  private insertGroups(newGroups: MessageGroup[], append = false) {
    if (!append) {
      newGroups.reverse();
    }

    for (const newGroup of newGroups) {
      // see if our new group belongs to the most recent group
      const group =
        this.messageGroups[append ? 0 : this.messageGroups.length - 1];

      if (group) {
        const lastMsgId = group.messages[group.messages.length - 1];
        const lastMsg = this.msgMap.get(lastMsgId);
        const newMsg = this.msgMap.get(newGroup.messages[0]);
        // if our message belongs to the previous group, in we go
        const groupCheck = this.isSameGroup(lastMsg, newMsg);
        if (groupCheck.same) {
          if (append) {
            group.messages.push(...newGroup.messages);
          } else {
            // historical messages are older, prepend to maintain chronological order
            group.messages.unshift(...newGroup.messages);
          }
        } else {
          // otherwise, just add our entire group as a new one
          if (append) {
            this.messageGroups.splice(0, 0, newGroup);
          } else {
            // update the boundary group's reason to reflect the new adjacency
            if (groupCheck.reason) {
              group.reason = groupCheck.reason;
            }
            this.messageGroups.push(newGroup);
          }
        }
      } else {
        if (append) {
          this.messageGroups.splice(0, 0, newGroup);
        } else {
          this.messageGroups.push(newGroup);
        }
      }
    }

    this.requestUpdate('messageGroups');
  }

  private groupMessages(msgIds: string[]): MessageGroup[] {
    // group our messages by origin and user
    const groups: MessageGroup[] = [];
    let lastGroup: MessageGroup = null;
    let lastMsg: ContactEvent = null;
    let lastTimeElapsedDate: Date = null;

    for (const msgId of msgIds) {
      const msg = this.msgMap.get(msgId);
      const groupCheck = this.isSameGroup(msg, lastMsg, lastTimeElapsedDate);

      if (!groupCheck.same || !lastGroup) {
        lastGroup = {
          messages: [],
          reason: groupCheck.reason || 'initial'
        };
        groups.push(lastGroup);

        // track when we last broke for time_elapsed
        if (groupCheck.reason === 'time_elapsed') {
          lastTimeElapsedDate = msg.created_on;
        }
      }

      lastGroup.messages.push(msgId);
      lastMsg = msg;
    }
    return groups;
  }

  private handleScroll(event: any) {
    const ele = event.target;
    const scrollableHeight = ele.scrollHeight - ele.clientHeight;

    if (scrollableHeight <= 0) {
      return;
    }

    // with column-reverse, scrollTop behavior depends on the browser
    // check if scrollTop is negative (some browsers) or positive (others)
    const absScrollTop = Math.abs(ele.scrollTop);

    // when scrolling up to older messages, absScrollTop increases
    // trigger when we're close to the maximum scroll (oldest messages)
    const shouldFetch = absScrollTop >= scrollableHeight - SCROLL_FETCH_BUFFER;

    this.hideTopScroll = absScrollTop >= scrollableHeight - 1;
    this.hideBottomScroll = absScrollTop <= 1;

    // hide notification when scrolled to bottom
    if (absScrollTop <= 10) {
      this.showNewMessageNotification = false;
    }

    if (shouldFetch) {
      this.fireCustomEvent(CustomEventType.ScrollThreshold);
    }

    // trigger when scrolling near the bottom (newest messages) in column-reverse
    if (absScrollTop <= SCROLL_FETCH_BUFFER * 3 && scrollableHeight > 0) {
      this.fireCustomEvent(CustomEventType.ScrollThresholdBottom);
    }
  }

  public scrollToBottom() {
    const scroll = this.shadowRoot.querySelector('.scroll');
    if (scroll) {
      scroll.scrollTop = 0;
      this.hideBottomScroll = true;
      this.showNewMessageNotification = false;
    }
  }

  public scrollToMessage(
    uuid: string,
    animate = true,
    onComplete?: () => void
  ) {
    this.highlightMessageUuid = uuid;
    window.setTimeout(() => {
      const scroll = this.shadowRoot.querySelector('.scroll') as HTMLElement;
      const row = this.shadowRoot.querySelector(
        `.row[data-uuid="${uuid}"]`
      ) as HTMLElement;
      if (scroll && row) {
        // manually set scrollTop to center the row within the scroll area
        // this avoids scrollIntoView which can move ancestor containers
        const scrollRect = scroll.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const rowCenter = rowRect.top + rowRect.height / 2;
        const scrollCenter = scrollRect.top + scrollRect.height / 2;
        const offset = rowCenter - scrollCenter;
        if (animate) {
          scroll.scrollBy({ top: offset, behavior: 'smooth' });
        } else {
          scroll.scrollTop = scroll.scrollTop + offset;
        }
      }
      if (onComplete) {
        // wait one frame for the browser to finish the scroll
        requestAnimationFrame(() => onComplete());
      }
    }, 150);
  }

  private highlightText(text: string, search: string): TemplateResult | string {
    if (!search || !text) {
      return text;
    }
    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
    if (parts.length === 1) {
      return text;
    }
    return html`${parts.map((part, index) =>
      index % 2 === 1 ? html`<mark>${part}</mark>` : part
    )}`;
  }

  private handleNewMessageClick() {
    this.scrollToBottom();
  }

  private getReasonLabel(reason: GroupReason): string {
    switch (reason) {
      case 'new_author':
        return '👤 Different author';
      case 'new_type':
        return '🔄 Message type changed';
      case 'time_elapsed':
      case 'initial':
      default:
        return '';
    }
  }

  private renderMessageGroup(
    group: MessageGroup,
    idx: number,
    lastShownTimestamp: Date | null
  ): { html: TemplateResult; timestamp: Date | null } {
    const today = new Date();
    const msgIds = group.messages;

    const mostRecentId = msgIds[msgIds.length - 1];
    const currentMsg = this.msgMap.get(mostRecentId);

    const incoming = this.agent
      ? currentMsg.type !== 'msg_received'
      : currentMsg.type === 'msg_received';

    const name = currentMsg._user?.name;

    const isMessageType =
      currentMsg.type === 'msg_received' ||
      currentMsg.type === 'msg_created' ||
      currentMsg.type === 'ivr_created' ||
      currentMsg.type === 'ticket_note_added';
    const showAvatar =
      this.avatars && ((isMessageType && this.agent) || !incoming);

    // resolve the identity shown in the avatar: prefer the user attached to
    // the event (an agent or flow author), otherwise fall back to the contact
    // for their own incoming messages.
    //
    // contact fallback assumes `_user` is absent for `msg_received` (contact
    // messages carry no `_user`, so first_name/last_name aren't available and
    // getFullName falls back to `name`); the fallback only applies when there
    // is no `_user` on the event.
    const fromContact = currentMsg.type === 'msg_received' && !currentMsg._user;
    const avatarName = currentMsg._user
      ? currentMsg._user.name
      : fromContact
        ? this.contactName
        : undefined;
    const avatarUuid = currentMsg._user
      ? currentMsg._user.uuid
      : fromContact
        ? this.contactUuid
        : undefined;

    // determine whether to fall back to the generic default (system) avatar.
    // when the event has a `_user`, preserve the original behavior exactly:
    // system iff that user has no uuid (a name-only flow author still gets the
    // default avatar). for a contact event with no `_user`, it's system only
    // when we have no contact identity at all.
    const isSystem = currentMsg._user
      ? !currentMsg._user.uuid
      : fromContact
        ? !this.contactUuid && !this.contactName
        : true;

    const reasonLabel = this.getReasonLabel(group.reason);
    const showReason = false; // reasonLabel && idx > 0;

    // determine if we should show a timestamp
    // use the first message in the group (oldest) for the timestamp
    const firstMsgId = msgIds[0];
    const firstMsg = this.msgMap.get(firstMsgId);

    // check if we should show a timestamp based on the last shown timestamp
    let showTimeForReason = false;
    let newLastShownTimestamp = lastShownTimestamp;

    if (idx > 0) {
      if (lastShownTimestamp) {
        const timeSinceLastShown = Math.abs(
          firstMsg.created_on.getTime() - lastShownTimestamp.getTime()
        );
        showTimeForReason = timeSinceLastShown >= BATCH_TIME_WINDOW;
      } else {
        showTimeForReason = group.reason === 'time_elapsed';
      }
    }

    let timeForReason = null;
    if (showTimeForReason) {
      newLastShownTimestamp = firstMsg.created_on;

      const isDifferentYear =
        today.getFullYear() !== firstMsg.created_on.getFullYear();
      const format = isDifferentYear
        ? VERBOSE_FORMAT_WITH_YEAR
        : today.getDate() !== firstMsg.created_on.getDate()
          ? VERBOSE_FORMAT
          : TIME_FORMAT;

      timeForReason = html`<div class="time time-elapsed">
        ${firstMsg.created_on.toLocaleTimeString(undefined, format)}
      </div>`;
    }

    const resultHtml = html`
      <div class="block  ${incoming ? 'incoming' : 'outgoing'}">
        <div class="group-messages" style="flex-grow:1">
          ${repeat(
            msgIds,
            (msgId) => msgId,
            (msgId, index) => {
              const msg = this.msgMap.get(msgId);
              const msgEvent = msg as MsgEvent;
              const statusClass = (msg as any)._status
                ? (msg as any)._status.status
                : '';
              const hasError =
                msgEvent.msg?.unsendable_reason ||
                (msgEvent._status?.reason &&
                  (statusClass === 'failed' || statusClass === 'errored'));
              const unsendableClass = hasError ? 'error' : '';
              const deletedClass = msgEvent._deleted ? 'deleted' : '';
              const latestClass = index === msgIds.length - 1 ? 'latest' : '';
              const eventClass = msg._rendered ? 'is-event' : '';
              const noteClass = msg.type === 'ticket_note_added' ? 'note' : '';
              const matchClass =
                this.highlightMessageUuid === msg.uuid ? 'search-match' : '';
              return html`<div
                class="row message ${statusClass} ${unsendableClass} ${deletedClass} ${latestClass} ${eventClass} ${noteClass} ${matchClass}"
                data-uuid=${msg.uuid || nothing}
              >
                ${this.renderMessage(
                  msg,
                  index == 0 ? name : null,
                  index === msgIds.length - 1
                )}
              </div>`;
            }
          )}
        </div>
        ${showAvatar
          ? html`<div class="avatar" style="align-self:flex-end">
              <temba-user
                uuid=${avatarUuid ?? nothing}
                name=${avatarName ?? nothing}
                first_name=${currentMsg._user?.first_name ?? nothing}
                last_name=${currentMsg._user?.last_name ?? nothing}
                avatar=${currentMsg._user?.avatar ?? nothing}
                ?system=${isSystem}
              >
              </temba-user>
            </div>`
          : null}
      </div>
      ${showReason
        ? html`<div class="group-reason">${reasonLabel}</div>`
        : null}
      ${timeForReason}
    `;

    return { html: resultHtml, timestamp: newLastShownTimestamp };
  }

  private renderNote(
    event: TicketEvent,
    name: string | null,
    isLast: boolean
  ): TemplateResult {
    return html`<div class="bubble-wrap">
      <div class="bubble">
        ${name ? html`<div class="name">${name}</div>` : null}
        <div style="white-space: pre-wrap;">${event.note}</div>
      </div>
      ${isLast
        ? html`<div class="note-time">
            <temba-date
              value=${event.created_on.toISOString()}
              display="relative"
            ></temba-date>
          </div>`
        : null}
    </div>`;
  }

  private renderMessage(
    event: ContactEvent,
    name = null,
    isLast = false
  ): TemplateResult {
    if (event._rendered) {
      return html`<div class="event">${event._rendered.html}</div>`;
    }

    if (event.type === 'ticket_note_added') {
      return this.renderNote(event as TicketEvent, name, isLast);
    }

    const message = event as MsgEvent;

    // safety check: if msg doesn't exist, return nothing
    if (!message.msg) {
      return html``;
    }

    const unsendableReason = message.msg?.unsendable_reason;
    const statusReason = message._status?.reason;
    const errorMessage = unsendableReason
      ? getUnsendableReasonMessage(unsendableReason)
      : statusReason
        ? getStatusReasonMessage(statusReason)
        : null;

    const logsURL =
      this.showMessageLogsAfter &&
      message.created_on >= this.showMessageLogsAfter &&
      message.msg.channel
        ? `/channels/channel/logs/${message.msg.channel.uuid}/msg/${event.uuid}/`
        : null;

    // handle deleted messages
    const isDeleted = message._deleted;
    const deletedByText = isDeleted
      ? message._deleted.by_contact
        ? 'contact'
        : message._deleted.user?.name || 'user'
      : null;

    // check if message has location attachment and text is just coordinates
    const hasLocationAttachment = message.msg.attachments?.some((att) =>
      att.startsWith('geo:')
    );
    const textIsCoordinates =
      hasLocationAttachment &&
      message.msg.text &&
      /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/.test(message.msg.text.trim());
    // Keep the text interpolation itself whitespace-free: this element uses
    // pre-wrap, so formatter-inserted newlines become visible in screenshots.
    const messageText =
      this.searchHighlight && this.highlightMessageUuid === message.uuid
        ? this.highlightText(message.msg.text, this.searchHighlight)
        : message.msg.text;

    return html`
      <div class="bubble-wrap">
        ${this.showTimestamps
          ? html`<div class="popup" style="white-space: nowrap;">
              ${errorMessage
                ? html`<div
                    style="color: var(--color-error); margin-right: 1em;"
                  >
                    ${errorMessage}
                  </div>`
                : null}
              <temba-date
                value="${message.created_on.toISOString()}"
                display="relative"
              ></temba-date>
              ${logsURL
                ? html`<a
                    style="margin-left: 1em; color: var(--color-primary-dark);"
                    href="${logsURL}"
                    target="_blank"
                    rel="noopener noreferrer"
                    ><temba-icon name="log"></temba-icon
                  ></a>`
                : null}

              <div class="arrow">▼</div>
            </div>`
          : null}
        ${isDeleted
          ? html`<div class="bubble">
              ${name ? html`<div class="name">${name}</div>` : null}
              <div class="message-deleted">
                Message deleted by ${deletedByText}
              </div>
            </div>`
          : message.msg.text && !textIsCoordinates
            ? html`<div class="bubble">
                ${name ? html`<div class="name">${name}</div>` : null}
                <div class="message-text">${messageText}</div>
              </div>`
            : null}

        <div class="attachments">
          ${(message.msg.attachments || []).map(
            (attachment) =>
              html`<temba-thumbnail
                attachment="${attachment}"
              ></temba-thumbnail>`
          )}
        </div>
      </div>
    `;
  }

  public reset() {
    this.msgMap.clear();
    this.messageGroups = [];
    this.hideBottomScroll = true;
    this.hideTopScroll = true;
    this.endOfHistory = false;
    this.oldestEventDate = null;
  }

  public setEndOfHistory(oldestDate: Date) {
    this.endOfHistory = true;
    this.oldestEventDate = oldestDate;
  }

  public render(): TemplateResult {
    return html` <div
      class="
        messages 
        ${this.hideBottomScroll ? 'scroll-at-bottom' : ''}
        ${this.hideTopScroll ? 'scroll-at-top' : ''}"
    >
      <div class="scroll" @scroll=${this.handleScroll}>
        ${this.messageGroups
          ? (() => {
              let lastTimestamp: Date | null = null;
              // process from oldest to newest (high index to low index)
              // to establish logical time groupings going forward in time
              const results = [];
              for (let idx = this.messageGroups.length - 1; idx >= 0; idx--) {
                const msgGroup = this.messageGroups[idx];
                const result = this.renderMessageGroup(
                  msgGroup,
                  idx,
                  lastTimestamp
                );
                lastTimestamp = result.timestamp;
                results.unshift(result.html); // add to front since we're going backwards
              }
              return results;
            })()
          : null}

        <temba-loading
          class="${!this.fetching ? 'hidden' : ''}"
        ></temba-loading>

        ${this.endOfHistory && this.oldestEventDate
          ? html`<div class="time first">
              ${this.oldestEventDate.toLocaleTimeString(
                undefined,
                VERBOSE_FORMAT
              )}
            </div>`
          : null}
      </div>
      ${!this.hasFooter
        ? html`<div
            class="new-message-notification ${this.showNewMessageNotification
              ? 'visible'
              : ''}"
            @click=${this.handleNewMessageClick}
          >
            New Messages
          </div>`
        : null}
      <slot class="header" name="header"></slot>
      <slot class="footer" name="footer"></slot>
    </div>`;
  }
}
